import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { VerticalLeftOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import BorderlessButton from '../common/buttons/BorderlessButton';
import UnassignedScheduleCategory from './UnassignedScheduleCategory';
import { moveShift } from './state/schedule.actions';

export default function UnassignedScheduleSidebar({
  shifts,
  onSidebarClose,
  onShiftSelect,
}) {
  const dispatch = useDispatch();
  const categories = useSelector((state) => state.schedule.categories);

  const relevantCategories = useMemo(() => (
    categories.concat([{
      id: null,
      name: 'Other',
      color: '#FFFFFF',
    }])
  ), [categories]);

  const onMoveRow = useCallback(async ({
    newCategoryId,
    oldId,
  }) => {
    await dispatch(moveShift(oldId, newCategoryId));
  }, []);

  const shiftCategoryMap = useMemo(() => {
    const shiftSet = new Set();
    return Object.values(shifts).flat().reduce((acc, shift) => {
      if (shift.isEvent || shift.users?.length || shiftSet.has(shift.id)) return acc;

      const relevantCategoryId = shift.shiftCategoryId || null;

      if (!acc[relevantCategoryId]) {
        acc[relevantCategoryId] = [];
      }

      acc[relevantCategoryId].push({
        ...shift,
        shiftCategoryId: relevantCategoryId,
      });
      shiftSet.add(shift.id);
      return acc;
    }, {});
  }, [shifts]);

  return (
    <div className="schedule-sidebar-container">
      <div className="schedule-sidebar-title">
        <div className="schedule-category-title">Unassigned Shifts</div>
        <BorderlessButton
          iconNode={<VerticalLeftOutlined />}
          onClick={onSidebarClose}
          style={{ background: 'transparent', width: 50, paddingRight: 0 }}
        />
      </div>
      <DndProvider backend={HTML5Backend}>
        {relevantCategories.map((category) => (
          <UnassignedScheduleCategory
            key={category.id}
            category={category}
            shifts={shiftCategoryMap[category.id]}
            onMoveRow={onMoveRow}
            onShiftSelect={onShiftSelect}
          />
        ))}
      </DndProvider>
    </div>
  );
}

UnassignedScheduleSidebar.propTypes = {
  shifts: PropTypes.shape({}).isRequired,
  onSidebarClose: PropTypes.func.isRequired,
  onShiftSelect: PropTypes.func.isRequired,
};
